import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Coins, CheckCircle2, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { API } from '../config/api';



export default function CoinSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuth();
  
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [coinsAdded, setCoinsAdded] = useState(0);
  const [newBalance, setNewBalance] = useState(0);
  const [error, setError] = useState(null);
  
  const pollCount = useRef(0);
  const maxPolls = 10;
  const pollInterval = 2000;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    checkPurchaseStatus();
  }, [sessionId, isAuthenticated]);

  const checkPurchaseStatus = async () => {
    if (pollCount.current >= maxPolls) {
      setStatus('pending');
      return;
    }

    pollCount.current += 1;

    try {
      const response = await fetch(`${API}/coins/purchase/status/${sessionId}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'completed') {
          setStatus('completed');
          setCoinsAdded(data.coins_added || 0);
          setNewBalance(data.new_balance || 0);
          return;
        }
        
        if (data.status === 'paid') {
          // Payment received but coins not yet added - keep polling
          setTimeout(checkPurchaseStatus, pollInterval);
          return;
        }
      }

      // Keep polling
      setTimeout(checkPurchaseStatus, pollInterval);
    } catch (error) {
      console.error('Error checking status:', error);
      setTimeout(checkPurchaseStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full glass-card border-border p-8 text-center" data-testid="coin-success-page">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-amber-400 animate-spin" />
            <h1 className="font-heading text-2xl font-bold mb-2">Processing Purchase</h1>
            <p className="text-muted-foreground mb-4">
              Please wait while we add coins to your wallet...
            </p>
            <div className="text-sm text-muted-foreground">
              Checking... ({pollCount.current}/{maxPolls})
            </div>
          </>
        )}

        {status === 'completed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-green-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Purchase Complete!</h1>
            <p className="text-muted-foreground mb-6">
              Your coins have been added to your wallet.
            </p>
            
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Coins className="w-6 h-6 text-amber-400" />
                <span className="text-2xl font-bold text-amber-400">+{coinsAdded.toLocaleString()}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                New Balance: <span className="text-amber-400 font-semibold">{newBalance.toLocaleString()} coins</span>
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate('/home')} className="w-full">
                <ArrowRight className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard?tab=earnings')}>
                View Wallet
              </Button>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-amber-400" />
            <h1 className="font-heading text-2xl font-bold mb-2">Payment Received</h1>
            <p className="text-muted-foreground mb-6">
              Your payment was successful! Coins will be added to your wallet shortly.
            </p>
            <Button onClick={() => navigate('/dashboard?tab=earnings')} className="w-full">
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="font-heading text-2xl font-bold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-6">
              {error || 'We could not verify your purchase. Please contact support if you were charged.'}
            </p>
            <Button onClick={() => navigate('/contact')} className="w-full">
              Contact Support
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
